/**
 * The ticket's activity trail.
 *
 * Rendered with the design system `Timeline`: one item per journal entry, in
 * the order the server returned them (the query asks for ascending, so the
 * ticket reads like a story from the top). The entry `kind` picks both the
 * headline and the tone, so a rejected fix looks different from a comment
 * without anyone reading the text.
 *
 * `is_internal` entries never reach the client — the contract filters them
 * server-side — so there is nothing to hide here.
 */

import { Timeline, type TimelineItem, Toggle, Typography } from "@norican/react";
import { useState } from "react";

import { EmptyStateBlock, useDateFormatter } from "@/components/common";
import { useT } from "@/i18n";

import type { TicketTimelineEntry, TicketTimelineEntryKind } from "../api/contract";
import { AUDIT_VALUE_CATALOGUE, isTicketStatusCode } from "../lib/ticket-display";
import { TranslatableTextView } from "./translatable-text";

type TimelineTone = NonNullable<TimelineItem["tone"]>;

const ENTRY_TONE: Record<TicketTimelineEntryKind, TimelineTone> = {
  CREATED: "accent",
  COMMENT: "default",
  STATUS_CHANGED: "accent",
  ASSIGNED: "default",
  ATTACHMENT_ADDED: "default",
  INFO_REQUESTED: "warning",
  INFO_PROVIDED: "accent",
  RESOLUTION_PROPOSED: "success",
  RESOLUTION_CONFIRMED: "success",
  RESOLUTION_REJECTED: "danger",
  APPROVAL_DECIDED: "accent",
  CLOSED: "success",
  REOPENED: "warning",
  SYSTEM: "default",
  UNKNOWN: "default",
};

export interface TicketActivityProps {
  entries: TicketTimelineEntry[];
}

/** The entries that are somebody talking, as opposed to the system narrating. */
const CONVERSATION_KINDS = new Set([
  "COMMENT",
  "INFO_REQUESTED",
  "INFO_PROVIDED",
  "RESOLUTION_PROPOSED",
]);

export function TicketActivity({ entries }: TicketActivityProps) {
  const { t, i18n } = useT("tickets");
  const { formatDateTime } = useDateFormatter();
  const [showAll, setShowAll] = useState(false);

  if (entries.length === 0) {
    return <EmptyStateBlock size="sm" title={t("timeline.empty")} description={null} />;
  }

  /* Two views over one feed, not two feeds.
     "Incident# 366 is assigned to your group" and a reply from the service desk
     are both timeline entries, but only one of them is a person talking to you —
     and on a ticket that has bounced between teams the replies are buried among
     six assignment notices. The toggle defaults to the conversation, because
     that is what someone opens a ticket to read; the full trail is one click
     away and nothing is hidden from it. */
  const conversation = entries.filter((entry) => CONVERSATION_KINDS.has(entry.kind));
  const hasBoth = conversation.length > 0 && conversation.length < entries.length;
  const shown = showAll || conversation.length === 0 ? entries : conversation;

  /**
   * One of Ivanti's values, in the portal's words when the portal has them.
   *
   * `Logged` and `Medium` are Ivanti's vocabulary; `status.LOGGED` and
   * `level.MEDIUM` are ours, and the ticket header beside this timeline uses
   * ours. An administrator can add a status the portal has no key for, and a
   * team or a person is a name rather than a code — both pass through
   * unchanged, because inventing a translation is worse than showing the
   * provider's own word.
   */
  const translateValue = (catalogue: "status" | "level" | "priority" | null, value: string) => {
    if (catalogue === null || value === "") {
      return value;
    }
    const key = `${catalogue}.${value.trim().toUpperCase().replace(/\s+/g, "_")}`;
    return i18n.exists(`tickets:${key}`) ? t(key as never) : value;
  };

  const items: TimelineItem[] = shown.map((entry) => {
    // An audit entry — a field Ivanti changed — carries its own sentence and
    // the two values, where a journal entry carries prose. `timeline.<kind>`
    // would render "Status changed" and drop the only part worth reading.
    const auditField = entry.label?.code?.startsWith("ticket.audit.")
      ? entry.label.code.slice("ticket.audit.".length)
      : null;
    const auditLine =
      auditField === null || entry.label === null
        ? null
        : t(`audit.${auditField}` as never, {
            from: translateValue(
              AUDIT_VALUE_CATALOGUE[auditField] ?? null,
              entry.label.params?.["from"] ?? "",
            ),
            to: translateValue(
              AUDIT_VALUE_CATALOGUE[auditField] ?? null,
              entry.label.params?.["to"] ?? "",
            ),
          });

    const movedTo = entry.metadata?.["to"];
    const statusChange =
      entry.kind === "STATUS_CHANGED" && movedTo !== undefined
        ? t("timeline.statusTo", {
            // An Ivanti administrator can add a status the portal has no key
            // for; showing the raw provider value beats showing nothing.
            status: isTicketStatusCode(movedTo) ? t(`status.${movedTo}`) : movedTo,
          })
        : null;

    const occurredAt = formatDateTime(entry.occurred_at);

    return {
      id: entry.entry_id,
      tone: ENTRY_TONE[entry.kind] ?? "default",
      // DS gap (workaround): `Timeline`'s timestamp slot is `flex: none;
      // white-space: nowrap` beside the title and exposes no class hook, so on
      // a phone "24 Sept 2026, 07:12" was clipped at the card edge. From `sm`
      // up it stays in the DS slot; below `sm` it moves into the title, which
      // wraps.
      timestamp: <span className="hidden sm:inline">{occurredAt}</span>,
      title: (
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span>{t(`timeline.${entry.kind}`)}</span>
          <Typography variant="caption" tone="muted" className="wrap-anywhere">
            {entry.actor.display_name}
          </Typography>
          <Typography
            as="span"
            variant="caption"
            tone="muted"
            className="basis-full font-mono font-normal sm:hidden"
          >
            {occurredAt}
          </Typography>
        </span>
      ),
      description: (
        // A `<div>`: a journal entry may be Ivanti rich text, whose blocks
        // cannot live inside a `<span>`. Timeline renders this into a `<div>`.
        <div className="flex flex-col gap-1">
          {auditLine === null ? null : (
            <Typography variant="body-sm" tone="muted">
              {auditLine}
            </Typography>
          )}
          {statusChange === null ? null : (
            <Typography variant="body-sm" tone="muted">
              {statusChange}
            </Typography>
          )}
          {entry.body == null ? null : <TranslatableTextView value={entry.body} multiline />}
          {entry.attachment == null ? null : (
            <Typography variant="body-sm" tone="muted">
              {entry.attachment.filename}
            </Typography>
          )}
        </div>
      ),
    };
  });

  return (
    <div className="flex flex-col gap-3">
      {hasBoth ? (
        <Toggle pressed={showAll} onPressedChange={setShowAll} size="sm" className="self-start">
          {t("timeline.showAll")}
        </Toggle>
      ) : null}

      <Timeline items={items} orientation="vertical" connector="rail" />
    </div>
  );
}
